import { Helmet } from 'react-helmet'
import { useLocation } from 'react-router'
// Components
import { useMaxWidth, Button } from '@tracklib/kit'
import AddToCollectionButton from 'components/track/addtocollectionbutton'
import AddToFavoritesButton from 'components/track/addtofavoritesbutton'
import AnimationWrapper from 'components/player/animationwrapper'
import Controls from 'components/player/controls'
import DownloadButton from 'components/base/downloadbutton'
import ErrorBoundary from 'errorboundary'
import FilterControl from 'components/player/filtercontrol'
import Info, { renderLink } from 'components/player/info'
import KeyboardShortcuts from 'components/player/keyboardshortcuts'
import MobileBar from 'components/player/mobilebar'
import ProgressBar from 'components/player/progressbar'
import VolumeControl from 'components/player/volumecontrol'
import Waveform from 'components/track/waveform'

// Hooks
import { useSongFromTrackId, useTrackFromTrackId } from 'modules/entities/hooks'
import { useFeatureToggle } from 'modules/featuretoggles/hooks'
import { useAudio } from 'modules/audio/hooks'

// Types
import type { Track } from 'types'

import styles from './player.css'

const Player = () => {
  const { pathname } = useLocation()
  const isSmall = useMaxWidth()
  const { trackId, songItemId, contextKey, loop } = useAudio()
  const song = useSongFromTrackId(trackId)
  const track = useTrackFromTrackId(trackId) as Track
  const audioFilterFeatureToggleEnabled = useFeatureToggle('audioFilter')
  if (!song || pathname.startsWith('/onboard')) {
    return null
  }

  return (
    <>
      <Helmet>
        <body data-playing="true" />
      </Helmet>
      <ErrorBoundary>
        <AnimationWrapper>
          {(expanded, setExpanded) => (
            <>
              <KeyboardShortcuts />
              <MobileBar song={song} track={track} expanded={expanded} setExpanded={setExpanded} />
              <ProgressBar invisible={expanded} />
              <div className={styles.inner}>
                {isSmall ? (
                  <ErrorBoundary>
                    <Info song={song} track={track} expanded />
                    <Waveform
                      track={track}
                      loop={loop}
                      contextKey={contextKey}
                      songItemId={songItemId}
                      showOnSmall
                      className={styles.waveformExpanded}
                    />
                    <Controls trackId={trackId} expanded />
                    <Button variant="outline" to={renderLink(song, track)} className={styles.goToTrackButton}>
                      Go to track page
                    </Button>
                  </ErrorBoundary>
                ) : (
                  <ErrorBoundary>
                    <Info song={song} track={track} />
                    <Controls trackId={trackId} />
                    <Waveform track={track} loop={loop} contextKey={contextKey} songItemId={songItemId} className={styles.waveform} />
                    <VolumeControl />
                    {audioFilterFeatureToggleEnabled && <FilterControl />}
                    <AddToFavoritesButton trackId={trackId} loop={loop} className={styles.button} />
                    <AddToCollectionButton trackId={trackId} loop={loop} className={styles.button} />
                    <DownloadButton trackId={trackId} loop={loop} expanded={!expanded} className={styles.downloadButton} />
                  </ErrorBoundary>
                )}
              </div>
            </>
          )}
        </AnimationWrapper>
      </ErrorBoundary>
    </>
  )
}

export default Player
